import React, { useEffect, useState } from 'react';
import FullPageLoadingSpinner from '../../pages/components/fullPageLoadingSpinner';
import {
  updateWaiverWorkItem,
  executeWaiverWorkItemAction,
} from '../../redux/components/waivers/workItem/waiverWorkItemDetailsActions';
import { associateWithWaiver, completeChangeWaiverAssociationAction, createEditExistingWaiverAction, editAirlineConfiguration, editNewWaiver, reopenWorkItem, resolveWorkItemAsNotWaiver, resolveWorkItemAsWaiver, resolveWorkItemAsWaiverMissing } from '../../redux/entities/waivers/workItems/waiverWorkItemActions';
import {
  selectUpdateEventStatus,
} from '../../redux/components/waivers/workItem/waiverWorkItemUpdateStatusSelectors';
import { selectWaiverToChangeAssociation } from '../../redux/components/waivers/workItem/waiverToChangeAssociationSelectors';
import { WaiverWorkItem, WaiverWorkItemDetails, WaiverWorkItemAction } from '../../types/waiverWorkItem';
import { Waiver } from '../../types/waiver';
import { updateWaiverChangeEventId } from '../../redux/entities/waivers/waivers/waiversActions';
import { getActionsForRecentWaivers, getChangeWaiverAssociationActions, getNonWaiverActions } from '../../redux/components/waivers/workItem/waiverWorkItemActionSelectors';
import WaiverWorkItemComponent from './WaiverWorkItemComponent';
import { setHelpPage } from '../../configTdm';
import { useDispatch, useSelector } from 'react-redux';
import { getWaiverWorkItemDetails } from '../../service/waiverWorkItemService';
import { getWaivers } from '../../service/waiverService';
import moment from 'moment';
import _ from 'lodash';
import { spinnerShow } from '../../lib/spinner';
import { spinnerHide } from '../../lib/spinner';
import { snoozeNotificationsForAirline } from '../../redux/components/waivers/workItem/waiverWorkItemDetailsActions';

const WaiverWorkItemContainer: React.FC = () => {
  const dispatch = useDispatch();

  const [workItemDetails, setWorkItemDetails] = useState<WaiverWorkItemDetails | null>(null);
  const [workItem, setWorkItem] = useState<WaiverWorkItem | null>(null);
  const [workItemWaivers, setWorkItemWaivers] = useState<Waiver[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [workItemActions, setWorkItemActions] = useState<WaiverWorkItemAction[] | null>(null);
  const waiverToChangeAssociation = useSelector(selectWaiverToChangeAssociation);
  const updateStatus = useSelector(selectUpdateEventStatus);

  const [feedbackValue, setFeedbackValue] = useState<string>('');

  const selectRecentWaivers = (waivers: Waiver[]): Waiver[] => {
      const oldestWaiverDate = moment().subtract(30, 'days');
      return _(waivers)
        .filter((waiver: Waiver) =>
          moment(waiver.publishedDate).isAfter(oldestWaiverDate),
        )
        .orderBy(['publishedDate'], ['desc'])
        .value();
    };

  const selectRecentWaiversByAirline = (waivers: Waiver[]): { [key: string]: Waiver[] } => {
    return _.groupBy(waivers, 'issuingAirlineCode') || {};
  }

  const getWaiverItemActions = (workItemDetails: WaiverWorkItemDetails | null, workItem: WaiverWorkItem | null, recentWaiversForAirline: { [key: string]: Waiver[] }, waiver: { [key: number]: Waiver }): WaiverWorkItemAction[] => {
    if (!workItemDetails || !workItem) {
      return [];
    }
    if (workItem.state === 'CLOSED') {
      return [
        reopenWorkItem,
        ...getChangeWaiverAssociationActions(
          workItem,
          workItemDetails,
          waiver,
        ),
      ];
    }
    if (!workItem.airlineCode) {
      return [editAirlineConfiguration, resolveWorkItemAsNotWaiver];
    }
    if (workItemDetails.waiverEnteredByAirline) {
      return [
        ...getActionsForRecentWaivers(
          workItemDetails,
          workItem,
          (airline) => recentWaiversForAirline[airline],
          associateWithWaiver,
        ),
        resolveWorkItemAsWaiverMissing,
      ];
    }
    const editExistingWaiversActions = getActionsForRecentWaivers(
      workItemDetails,
      workItem,
      (airline) => recentWaiversForAirline[airline],
      createEditExistingWaiverAction,
    );
    const nonWaiverActions = getNonWaiverActions(workItem);
    return [
      editNewWaiver,
      ...editExistingWaiversActions,
      resolveWorkItemAsWaiver,
      ...nonWaiverActions,
    ];
  }

  const selectWorkItemWaivers = (workItem: WaiverWorkItem | undefined, waiversById: Record<string, Waiver>) => {
      if (!workItem) {
        return [];
      }
      return _(waiversById)
        .pick(Array.from(workItem.associatedWaiverIds))
        .values()
        .compact()
        .value();
    };

  useEffect(() => {
    spinnerShow();
    const workItemId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    (async () => {
      try {
      const workItemDetails = await getWaiverWorkItemDetails(Number(workItemId));
      const workItemWaivers = await getWaivers();
      setWorkItemDetails(workItemDetails.workItemDetails);
      setWorkItem(workItemDetails.workItem);
      const recentWaivers = selectRecentWaivers(workItemWaivers)
      const recentWaiversByAirline = selectRecentWaiversByAirline(recentWaivers);
      const waiversById = _.keyBy(workItemWaivers, 'id');
      setWorkItemWaivers(selectWorkItemWaivers(workItemDetails.workItem, waiversById));
      const workItemActions = getWaiverItemActions(workItemDetails.workItemDetails, workItemDetails.workItem, recentWaiversByAirline, waiversById);
      setWorkItemActions(workItemActions);
      spinnerHide();
      } catch (error) {
        setErrorMessage('Error loading waiver work item details');
        spinnerHide();
      }
    })();
    // dispatch(loadComponent(Number(workItemId)) as any);
    setHelpPage('helpWaiverWorkItemList');
  }, []);

  useEffect(() => {
    if (workItem) {
      setFeedbackValue(workItem.feedback ?? '');
    }
  }, [workItem]);

  const feedbackOnSubmit = () => {
    if (workItem) {
      const waiverWorkItem: Partial<WaiverWorkItem> = {
        ...workItem,
        feedback: feedbackValue,
      };
      dispatch(updateWaiverWorkItem(waiverWorkItem as WaiverWorkItem) as any);
    }
  };

  const feedbackOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedbackValue(e.currentTarget.value);
  };

  const executeAction = (action: WaiverWorkItemAction, workItem: WaiverWorkItem) => {
    dispatch(executeWaiverWorkItemAction(action, workItem) as any);
  };

  const changeWaiverAssociation = (waiver: Waiver, changeEventId: number) => {
    dispatch(updateWaiverChangeEventId({ waiver, changeEventId }) as any);
  };

  const endChangeWaiverAssociation = (workItem: WaiverWorkItem) => {
    dispatch(executeWaiverWorkItemAction(completeChangeWaiverAssociationAction(), workItem) as any);
  };

  const snoozeNotificationForAirline = (airlineCode: string, hours: number) => {
    dispatch(snoozeNotificationsForAirline(airlineCode, hours) as any);
  };

  if (loading) {
    return <FullPageLoadingSpinner />;
  }

  return workItemDetails && workItem ? (
    <WaiverWorkItemComponent
      workItem={workItem}
      workItemDetails={workItemDetails}
      workItemWaivers={workItemWaivers ?? []}
      workItemActions={workItemActions ?? []}
      executeAction={executeAction}
      waiverToChangeAssociation={waiverToChangeAssociation ?? null}
      changeWaiverAssociation={changeWaiverAssociation}
      endChangeWaiverAssociation={() => endChangeWaiverAssociation(workItem)}
      feedbackOnSubmit={feedbackOnSubmit}
      feedbackOnChange={feedbackOnChange}
      feedbackValue={feedbackValue}
      snoozeNotificationsForAirline={snoozeNotificationForAirline}
      updateStatus={updateStatus}
    />
  ) : null;
};


export default WaiverWorkItemContainer;